import React, { useState } from 'react';
import styled from 'styled-components';
import { authService } from '../services/auth';
import HelpModal from './HelpModal';

const HeaderContainer = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 2rem;
  height: 70px;
  width: 100%;
  box-sizing: border-box;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(225, 232, 237, 0.6);
  position: fixed;
  top: 0;
  left: 0;
  z-index: 200;
  transition: all 0.3s ease;
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Logo = styled.img`
  height: 36px;
  width: auto;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.4rem;
  font-weight: 600;
  font-family: 'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  color: #000000;
  letter-spacing: -0.3px;
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  border-radius: 50px;
  background: rgba(0, 58, 112, 0.07);
  border: 1px solid rgba(0, 58, 112, 0.15);
`;

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #003A70;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-family: 'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  font-size: 0.9rem;
`;

const UserName = styled.span`
  font-weight: 500;
  color: #000000;
  font-size: 0.95rem;
  font-family: 'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
`;

const HelpButton = styled.button`
  background: transparent;
  color: #6b7280;
  border: 1px solid #e5e7eb;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
    color: #374151;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const LogoutButton = styled.button`
  background: transparent;
  color: #6b7280;
  border: 1px solid #e5e7eb;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
    color: #374151;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const AppHeader: React.FC = () => {
  const user = authService.getUser();
  const [helpOpen, setHelpOpen] = useState(false);

  const handleLogout = () => {
    if (window.confirm('Möchten Sie sich wirklich ausloggen?')) {
      authService.logout();
    }
  };

  const getInitials = (username: string) => {
    return username
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <>
      <HeaderContainer>
        <Brand>
          <Logo src="/logo_berlin_m_srgb.svg" alt="Logo" />
          <Title>GSTU Needle</Title>
        </Brand>
        <UserSection>
          <UserInfo>
            <Avatar>
              {getInitials(user?.username || 'User')}
            </Avatar>
            <UserName>{user?.username || 'User'}</UserName>
          </UserInfo>
          <HelpButton onClick={() => setHelpOpen(true)} aria-label="Hilfe">?</HelpButton>
          <LogoutButton onClick={handleLogout}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16,17 21,12 16,7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            Logout
          </LogoutButton>
        </UserSection>
      </HeaderContainer>
      <HelpModal isOpen={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
};

export default AppHeader;
